import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'

/** 单个 Plugin/Contributor/Pass 的图形要求不兼容项。 */
export interface GraphicsRequirementIssue {
  readonly subjectType: string
  readonly subjectName: string
  readonly currentBackend: GraphicsBackendKind
  readonly supportedBackends: readonly GraphicsBackendKind[]
  readonly missingCapabilities: readonly string[]
  /**
   * 静态预检时无法判定的声明（例如尚未拿到设备 feature/limit）。
   *
   * 这些项**不能**当作通过：控制卡需要把它们呈现为「需运行时确认」。
   */
  readonly unresolvedRequirements?: readonly string[]
}

/** 图形底座结构化错误基类。 */
export class GraphicsError extends Error {
  readonly code: string

  constructor(code: string, message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = new.target.name
    this.code = code
  }
}

/** 调用方请求的 backend 与实际对象不一致。 */
export class BackendMismatchError extends GraphicsError {
  readonly expected: GraphicsBackendKind
  readonly actual: GraphicsBackendKind
  readonly operation?: string

  constructor(expected: GraphicsBackendKind, actual: GraphicsBackendKind, operation?: string) {
    super(
      'GRAPHICS_BACKEND_MISMATCH',
      `图形后端不匹配：${operation ? `${operation} 要求 ` : ''}${expected}，实际为 ${actual}`
    )
    this.expected = expected
    this.actual = actual
    this.operation = operation
  }
}

/** 当前 backend 不支持调用方请求的图形能力。 */
export class UnsupportedGraphicsCapabilityError extends GraphicsError {
  readonly capability: string
  readonly backend?: GraphicsBackendKind

  constructor(capability: string, backend?: GraphicsBackendKind, reason?: string) {
    super(
      'UNSUPPORTED_GRAPHICS_CAPABILITY',
      `不支持图形能力 "${capability}"${backend ? `（backend=${backend}）` : ''}${
        reason ? `：${reason}` : ''
      }`
    )
    this.capability = capability
    this.backend = backend
  }
}

/** 一次性汇总的插件或动态扩展图形要求错误。 */
export class GraphicsRequirementsError extends GraphicsError {
  readonly issues: readonly GraphicsRequirementIssue[]

  constructor(issues: readonly GraphicsRequirementIssue[]) {
    const frozenIssues = issues.map((issue) =>
      Object.freeze({
        ...issue,
        supportedBackends: Object.freeze([...issue.supportedBackends]),
        missingCapabilities: Object.freeze([...issue.missingCapabilities]),
      })
    )
    super(
      'GRAPHICS_REQUIREMENTS_NOT_MET',
      `图形要求不兼容：${frozenIssues
        .map((issue) => {
          const missing =
            issue.missingCapabilities.length > 0
              ? `，缺失能力=[${issue.missingCapabilities.join(', ')}]`
              : ''
          return `${issue.subjectType} "${issue.subjectName}" 当前 backend=${issue.currentBackend}，支持 backend=[${issue.supportedBackends.join(', ')}]${missing}`
        })
        .join('；')}`
    )
    this.issues = Object.freeze(frozenIssues)
  }
}

/** 当前构建尚未提供或当前平台不能提供请求的 backend。 */
export class UnsupportedGraphicsBackendError extends GraphicsError {
  readonly backend: GraphicsBackendKind
  readonly reason: string

  constructor(backend: GraphicsBackendKind, reason: string) {
    super('UNSUPPORTED_GRAPHICS_BACKEND', `无法创建 ${backend} 图形后端：${reason}`)
    this.backend = backend
    this.reason = reason
  }
}

/** WebGPU 创建流程在明确阶段失败；details 只能包含安全、可序列化的诊断值。 */
export class WebGPUInitializationError extends GraphicsError {
  readonly backend = 'webgpu' as const
  readonly stage: string
  readonly details: Readonly<Record<string, string | number | boolean>>

  constructor(
    stage: string,
    message: string,
    details: Record<string, string | number | boolean> = {},
    options?: ErrorOptions
  ) {
    super('WEBGPU_INITIALIZATION_FAILED', `WebGPU 初始化失败（${stage}）：${message}`, options)
    this.stage = stage
    this.details = Object.freeze({ ...details })
  }
}

/** engine-owned WebGPU device 进入不可恢复的 lost 状态。 */
export class WebGPUDeviceLostError extends GraphicsError {
  readonly reason: string | null

  constructor(message: string, reason: string | null) {
    super(
      'WEBGPU_DEVICE_LOST',
      `WebGPU device lost：${message}${reason ? `（reason=${reason}）` : ''}`
    )
    this.reason = reason
  }
}

/** 判别式配置中包含互相冲突的选项。 */
export class GraphicsConfigConflictError extends GraphicsError {
  readonly conflictingKeys: readonly string[]

  constructor(message: string, conflictingKeys: readonly string[]) {
    super('GRAPHICS_CONFIG_CONFLICT', message)
    this.conflictingKeys = Object.freeze([...conflictingKeys])
  }
}

/** surface DPR 已由另一个 owner 独占。 */
export class GraphicsResolutionOwnerConflictError extends GraphicsError {
  constructor() {
    super(
      'GRAPHICS_RESOLUTION_OWNER_CONFLICT',
      'surface resolution 已由另一个 owner 持有，请先释放现有 GraphicsResolutionLease'
    )
  }
}

/** GPU 提交发生在非法生命周期或 frame generation。 */
export class GraphicsSubmissionError extends GraphicsError {
  readonly generation?: number

  constructor(message: string, generation?: number) {
    super('GRAPHICS_SUBMISSION_REJECTED', message)
    this.generation = generation
  }
}

/** 旧的区域采集请求被更新的请求覆盖（newest-wins）时抛出。 */
export class GraphicsCaptureSupersededError extends GraphicsError {
  constructor() {
    super('GRAPHICS_CAPTURE_SUPERSEDED', '区域采集请求已被更新的请求覆盖')
  }
}
